import { Box, Code, Flex, Heading, Icon, List, Separator, Table, Text } from '@chakra-ui/react';

import type { TocItem } from '#features/blog/components/TableOfContents';
import { Image, Panel, TriangleAlertIcon } from '#features/ui';

interface EditorJsBlock {
  id?: string;
  type: string;
  data: Record<string, unknown>;
}

interface EditorJsContent {
  time?: number;
  blocks: EditorJsBlock[];
  version?: string;
}

interface EditorJsRendererProps {
  content: EditorJsContent;
  tocItems?: TocItem[];
  headingOffset?: number;
}

// Ссылки внутри текста приходят готовым HTML и не получают рецепт link: без этого они того же
// цвета, что абзац, и без подчёркивания. purple.fg на фоне страницы ≈4.4:1, поэтому одного
// цвета мало — подчёркивание обязательно.
const articleLinks = {
  '& a[href]': {
    color: 'purple.fg',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  '& a[href]:hover': { textDecorationThickness: '2px' },
};

export const EditorJsRenderer = ({
  content,
  tocItems,
  headingOffset = 0,
}: EditorJsRendererProps) => {
  let headingIndex = headingOffset - 1;

  return (
    <Flex direction="column" gap={0} css={articleLinks}>
      {content.blocks.map((block, index) => {
        let tocId: string | undefined;
        if (
          block.type === 'header' &&
          ((block.data.level as number) === 2 || (block.data.level as number) === 3)
        ) {
          headingIndex++;
          tocId = tocItems?.[headingIndex]?.id;
        }
        return <EditorJsBlock key={block.id ?? index} block={block} tocId={tocId} />;
      })}
    </Flex>
  );
};

const EditorJsBlock = ({ block, tocId }: { block: EditorJsBlock; tocId?: string }) => {
  switch (block.type) {
    case 'header':
      return <HeaderBlock data={block.data} tocId={tocId} />;
    case 'paragraph':
      return <ParagraphBlock data={block.data} />;
    case 'list':
      return <ListBlock data={block.data} />;
    case 'quote':
      return <QuoteBlock data={block.data} />;
    case 'delimiter':
      return <Separator my={6} />;
    case 'image':
      return <ImageBlock data={block.data} />;
    case 'table':
      return <TableBlock data={block.data} />;
    case 'code':
      return <CodeBlock data={block.data} />;
    case 'checklist':
      return <ChecklistBlock data={block.data} />;
    case 'warning':
      return <WarningBlock data={block.data} />;
    case 'embed':
      return <EmbedBlock data={block.data} />;
    case 'raw':
      return <RawBlock data={block.data} />;
    case 'beforeAfter':
      return <BeforeAfterBlock data={block.data} />;
    case 'statComparison':
      return <StatComparisonBlock data={block.data} />;
    case 'tierList':
      return <TierListBlock data={block.data} />;
    default:
      return null;
  }
};

const HeaderBlock = ({ data, tocId }: { data: Record<string, unknown>; tocId?: string }) => {
  const level = (data.level as number) ?? 2;
  const text = data.text as string;
  const tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  // Шкала статьи живёт в теме (`text-styles.ts`, article.*): h1 внутри текста редакция не
  // ставит (название рисует BlogPost), а h5 и h6 остаются на ступени h4.
  const styleMap: Record<number, string> = { 1: 'article.h1', 2: 'article.h2', 3: 'article.h3' };
  const mtMap: Record<number, number> = { 1: 10, 2: 12, 3: 8, 4: 6 };

  return (
    <Heading
      as={tag}
      id={tocId}
      textStyle={styleMap[level] ?? 'article.h4'}
      mt={mtMap[level] ?? 6}
      mb={4}
      scrollMarginTop="80px"
    >
      <span dangerouslySetInnerHTML={{ __html: text }} />
    </Heading>
  );
};

const ParagraphBlock = ({ data }: { data: Record<string, unknown> }) => {
  const text = data.text as string;
  if (!text) return null;

  return (
    <Text textStyle="article.body" mb={6} color="fg">
      <span dangerouslySetInnerHTML={{ __html: text }} />
    </Text>
  );
};

const ListBlock = ({ data }: { data: Record<string, unknown> }) => {
  const style = data.style as string;
  const items = data.items as string[];
  const isOrdered = style === 'ordered';

  return (
    <List.Root
      as={isOrdered ? 'ol' : 'ul'}
      ps={isOrdered ? 7 : 6}
      gap={2}
      mb={6}
      textStyle="article.body"
    >
      {items.map((item, i) => (
        <List.Item key={i}>
          <span dangerouslySetInnerHTML={{ __html: item }} />
        </List.Item>
      ))}
    </List.Root>
  );
};

// Цитата — вложенный блок статьи, а не карточка: тихая заливка `nested` без рамки и без
// цветной полосы слева (цветной кант на карточке в системе под запретом).
const QuoteBlock = ({ data }: { data: Record<string, unknown> }) => {
  const text = data.text as string;
  const caption = data.caption as string | undefined;

  return (
    <Panel as="blockquote" variant="nested" px={5} py={4} my={6}>
      <Text textStyle="article.quote" color="fg.emphasized">
        <span dangerouslySetInnerHTML={{ __html: text }} />
      </Text>
      {caption && (
        <Text textStyle="body" mt={3} color="fg.muted">
          – <span dangerouslySetInnerHTML={{ __html: caption }} />
        </Text>
      )}
    </Panel>
  );
};

const ImageBlock = ({ data }: { data: Record<string, unknown> }) => {
  const file = data.file as { url: string } | undefined;
  const url = (file?.url ?? data.url) as string | undefined;
  const caption = data.caption as string | undefined;

  if (!url) return null;

  return (
    <Flex as="figure" direction="column" gap={2} my={6}>
      <Box borderRadius="xl" overflow="hidden">
        <Image src={url} alt={caption ?? ''} width={1000} />
      </Box>
      {caption && (
        <Text as="figcaption" textStyle="body" color="fg.muted" textAlign="center">
          <span dangerouslySetInnerHTML={{ __html: caption }} />
        </Text>
      )}
    </Flex>
  );
};

const TableBlock = ({ data }: { data: Record<string, unknown> }) => {
  const content = data.content as string[][];
  const withHeadings = data.withHeadings as boolean | undefined;

  if (!content?.length) return null;

  // Три колонки и больше на телефоне ужимались в узкие столбики и тянули таблицу вниз:
  // даём минимальную ширину и горизонтальный скролл. `overflow="hidden"` рядом с
  // `overflowX="auto"` перебивал скролл, скругление и так обрезает любой overflow, кроме visible.
  const wide = (content[0]?.length ?? 0) >= 3;

  return (
    <Box overflowX="auto" my={6} borderRadius="lg">
      <Table.Root size="sm" variant="outline" minW={wide ? '36rem' : undefined}>
        {withHeadings && content.length > 0 && (
          <Table.Header>
            <Table.Row>
              {content[0].map((cell, i) => (
                <Table.ColumnHeader key={i} p={3} textStyle="title">
                  <span dangerouslySetInnerHTML={{ __html: cell }} />
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
        )}
        <Table.Body>
          {content.slice(withHeadings ? 1 : 0).map((row, ri) => (
            <Table.Row key={ri}>
              {row.map((cell, ci) => (
                <Table.Cell key={ci} p={3} textStyle="body" verticalAlign="top">
                  <span dangerouslySetInnerHTML={{ __html: cell }} />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

const CodeBlock = ({ data }: { data: Record<string, unknown> }) => {
  const code = data.code as string;

  return (
    <Box my={6} borderRadius="lg" overflow="hidden">
      <Code
        display="block"
        whiteSpace="pre-wrap"
        p={5}
        textStyle="sm"
        lineHeight="1.6"
        bg="brand.800"
        color="fg"
        width="100%"
      >
        {code}
      </Code>
    </Box>
  );
};

interface ChecklistItem {
  text: string;
  checked: boolean;
}

const ChecklistBlock = ({ data }: { data: Record<string, unknown> }) => {
  const items = data.items as ChecklistItem[];

  return (
    <Flex direction="column" gap={2} my={6}>
      {items.map((item, i) => (
        <Flex key={i} gap={2} alignItems="flex-start">
          <Text textStyle="article.body">{item.checked ? '☑' : '☐'}</Text>
          <Text
            textStyle="article.body"
            textDecoration={item.checked ? 'line-through' : 'none'}
            color={item.checked ? 'fg.muted' : 'fg'}
          >
            <span dangerouslySetInnerHTML={{ __html: item.text }} />
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const WarningBlock = ({ data }: { data: Record<string, unknown> }) => {
  const title = data.title as string;
  const message = data.message as string;

  if (!title && !message) return null;

  // Нейтральная плита, а не жёлтая заливка с цветным кантом: статусные цвета в системе живут на
  // значках и точках, не на подложках. Предупреждение несёт значок в `fg.warning`, сам текст
  // остаётся текстом статьи. Значок декоративный: смысл уже в заголовке и тексте.
  return (
    <Panel as="aside" role="note" variant="object" display="flex" gap={3} p={5} my={6}>
      <Icon as={TriangleAlertIcon} boxSize={5} color="fg.warning" mt={1} flexShrink={0} />
      <Box minW={0}>
        {title && (
          <Text textStyle="lead" mb={2} color="fg">
            <span dangerouslySetInnerHTML={{ __html: title }} />
          </Text>
        )}
        {message && (
          <Text textStyle="article.body" color="fg.emphasized">
            <span dangerouslySetInnerHTML={{ __html: message }} />
          </Text>
        )}
      </Box>
    </Panel>
  );
};

const EmbedBlock = ({ data }: { data: Record<string, unknown> }) => {
  const embed = data.embed as string | undefined;
  const source = data.source as string | undefined;
  const caption = data.caption as string | undefined;
  const width = data.width as number | undefined;
  const height = data.height as number | undefined;

  const src = embed || source;
  if (!src) return null;

  return (
    <Flex as="figure" direction="column" gap={2} my={6}>
      <Box
        borderRadius="xl"
        overflow="hidden"
        position="relative"
        css={{ aspectRatio: `${width ?? 16} / ${height ?? 9}` }}
      >
        <iframe
          src={src}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          allowFullScreen
          loading="lazy"
        />
      </Box>
      {caption && (
        <Text as="figcaption" textStyle="body" color="fg.muted" textAlign="center">
          <span dangerouslySetInnerHTML={{ __html: caption }} />
        </Text>
      )}
    </Flex>
  );
};

const RawBlock = ({ data }: { data: Record<string, unknown> }) => {
  const html = data.html as string;
  if (!html) return null;

  return <Box my={6} dangerouslySetInnerHTML={{ __html: html }} />;
};

const BeforeAfterBlock = ({ data }: { data: Record<string, unknown> }) => {
  const beforeUrl = data.beforeUrl as string | undefined;
  const afterUrl = data.afterUrl as string | undefined;
  const beforeLabel = (data.beforeLabel as string) || 'Before';
  const afterLabel = (data.afterLabel as string) || 'After';

  if (!beforeUrl || !afterUrl) return null;

  return (
    <Flex direction="column" gap={3} my={6}>
      <Flex gap={3}>
        <Box flex={1} position="relative">
          <Box borderRadius="xl" overflow="hidden">
            <Image src={beforeUrl} alt={beforeLabel} width={500} />
          </Box>
          <Box
            position="absolute"
            top={2}
            left={2}
            bg="blackAlpha.700"
            px={3}
            py={1}
            borderRadius="md"
          >
            <Text textStyle="label">{beforeLabel}</Text>
          </Box>
        </Box>
        <Box flex={1} position="relative">
          <Box borderRadius="xl" overflow="hidden">
            <Image src={afterUrl} alt={afterLabel} width={500} />
          </Box>
          <Box
            position="absolute"
            top={2}
            right={2}
            bg="blackAlpha.700"
            px={3}
            py={1}
            borderRadius="md"
          >
            <Text textStyle="label">{afterLabel}</Text>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};

interface StatComparisonItem {
  name: string;
  before: string;
  after: string;
  unit?: string;
}

const StatComparisonBlock = ({ data }: { data: Record<string, unknown> }) => {
  const title = data.title as string | undefined;
  const items = data.items as StatComparisonItem[];

  if (!items?.length) return null;

  return (
    <Panel variant="object" p={5} my={6}>
      {title && (
        <Text textStyle="lead" mb={4}>
          {title}
        </Text>
      )}
      <Flex direction="column" gap={0}>
        {items.map((item, i) => {
          const before = parseFloat(item.before);
          const after = parseFloat(item.after);
          const isNerf = !isNaN(before) && !isNaN(after) && after < before;
          const isBuff = !isNaN(before) && !isNaN(after) && after > before;

          return (
            <Flex
              key={i}
              justifyContent="space-between"
              alignItems="center"
              py={3}
              borderBottomWidth={i < items.length - 1 ? '1px' : '0'}
              borderColor="border.subtle"
            >
              <Text fontWeight="500">{item.name}</Text>
              <Flex gap={2} alignItems="center">
                <Text color="fg.muted">
                  {item.before}
                  {item.unit}
                </Text>
                <Text color="fg.subtle">→</Text>
                <Text fontWeight="600" color={isBuff ? 'green.400' : isNerf ? 'red.400' : 'fg'}>
                  {item.after}
                  {item.unit}
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Panel>
  );
};

const TIER_COLORS: Record<string, string> = {
  S: 'red.400',
  A: 'orange.400',
  B: 'yellow.400',
  C: 'green.400',
  D: 'blue.400',
};

const TierListBlock = ({ data }: { data: Record<string, unknown> }) => {
  const title = data.title as string | undefined;
  const tiers = data.tiers as Record<string, string[]>;

  if (!tiers) return null;

  return (
    <Panel variant="object" p={5} my={6}>
      {title && (
        <Text textStyle="lead" mb={4}>
          {title}
        </Text>
      )}
      <Flex direction="column" gap={3}>
        {Object.entries(tiers).map(([tier, items]) => (
          <Flex key={tier} gap={3} alignItems="center">
            <Flex
              w="40px"
              h="40px"
              borderRadius="md"
              bg="bg.muted"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Text fontWeight="800" fontSize="lg" color={TIER_COLORS[tier] ?? 'fg'}>
                {tier}
              </Text>
            </Flex>
            <Flex gap={2} flexWrap="wrap">
              {items.map((item) => (
                <Box key={item} bg="bg.muted" px={3} py={1} borderRadius="md" textStyle="sm">
                  {item}
                </Box>
              ))}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Panel>
  );
};
